// Country config — the source-of-truth map for per-country widget behavior.
//
// Read at engage time from the mount element's `data-cc-countrycode` attribute
// (see main.tsx#readCountryCode). Any element that carries `data-cc-book-now`,
// `data-cc-inline-widget`, or `data-cc-booking-widget` may also carry
// `data-cc-countrycode="us|uk|ca|au"`; the widget resolves that to a
// CountryConfig and applies its values.
//
// Country → default starter promo mapping (2026-08-07, codes revised
// 2026-10-17):
//   US → CUSTOMFIT    — the prod code
//   UK → CUSTOMFITUK  — what every UK store's /fittings reports as active
//   CA → CUSTOMFITCA  — likewise for Canada
//   AU → CUSTOMFITAU  — likewise for Australia
//
// The FITTINGxx codes these started as were "expected in SF" and never
// materialised: on 2026-10-17 production /promos/validate rejected all three
// while CUSTOMFITxx validated at the $100 full-bag price. The starter is only
// the pre-studio guess (SELECT_LOCATION adopts the store's own active code),
// so the stale codes cost nothing visible, but a guess should be a real code.
//
// data-cc-promoverride on the mount element still wins over these defaults —
// that's the escape hatch for campaign-specific pages and for QA to force any
// promo through the same plumbing. Updating this file is the only widget-side
// change needed when new per-country codes ship.

export type CountryCode = 'us' | 'uk' | 'ca' | 'au';

export interface CountryConfig {
  /** ISO 2-letter code — echoed back to widget code paths that need the raw
   * identifier (e.g. `listLocations(country)` middleware query). */
  code: CountryCode;
  /** Starter promo dispatched on widget engage to hydrate per-country pricing. */
  initialPromoCode: string;
  /** ISO 4217 currency code — used by Intl.NumberFormat for price display. */
  currency: string;
  /** BCP-47 locale — used by Intl.NumberFormat and, later, label lookup. */
  locale: string;
  /** Postal-code field label. Swaps "Zip Code" (US/CA) for "Postcode" (UK/AU). */
  /** Each market's own word for it. The UK writes it as two ("Post Code"). */
  postalCodeLabel: 'Zip Code' | 'Postcode' | 'Post Code';
  /** Distance unit used in the location-picker "within X miles/km" label. */
  distanceUnit: 'mi' | 'km';
  /** Max search radius shown in the picker, expressed in the country's
   * distanceUnit. For small-store-count countries (UK/CA/AU) this is
   * intentionally large so all in-country stores appear regardless of
   * the user's postcode. */
  maxSearchRadius: number;
  /** Whether the marketing-email opt-in checkbox is pre-checked. Per the
   * CC requirements sheet all four countries pre-check; kept as a
   * per-country field to make future divergence a config change, not code. */
  emailOptInDefaultChecked: boolean;
  /**
   * Market-specific marketing-consent copy. Absent means the US wording, which
   * is TCPA language — autodialer, "not a condition of purchase", HELP/STOP —
   * and describes obligations that only exist in the United States. Reading it
   * to an Australian customer misstates what they are agreeing to.
   *
   * Email and SMS stay two separate opt-ins with two separate consents; this
   * only changes what each one says.
   */
  consent?: {
    /**
     * Section heading and intro above the checkboxes. The UK rewords a single
     * line and wants the checkbox to stand alone, so both are optional and
     * omitting them renders no heading at all.
     */
    heading?: string;
    intro?: string;
    /**
     * Bold label on its own line above the sentence. Omitted where the
     * checkbox is one plain sentence (UK, AU).
     */
    emailLabel?: string;
    emailBody: string;
    /** Both required together, and only rendered where smsOptInOffered. */
    smsLabel?: string;
    smsBody?: string;
    /** Footnote either side of the Privacy Policy link. */
    footnotePrefix?: string;
    footnoteSuffix?: string;
    /**
     * The market collects email + SMS marketing consent as ONE checkbox rather
     * than two. Australia only: the client asked for a single box reading
     * "marketing emails and sms", which drives BOTH the email and SMS opt-ins.
     * When set, `emailBody` is that combined box's copy, the separate SMS box
     * is not rendered, and the booking payload sends both opt-ins from this one
     * box's state. See marketingConsentCombined() below.
     */
    combined?: boolean;
  };
  /**
   * A consent the customer must actively give before Step 1 will submit.
   *
   * UK only. Deliberately NOT a third slot on `consent` above: that block is
   * marketing opt-in — optional, pre-ticked, and recorded against the lead.
   * This is the opposite on every count. It starts unticked, it blocks the
   * form, and it is stored nowhere. CC's instruction was explicit: "we don't
   * need to record any info in the system it is just a block."
   *
   * Reusing the SMS slots to carry it would have made the config lie about
   * what it holds, and AU uses those slots for actual SMS consent.
   */
  requiredConsent?: {
    /** Copy either side of the Privacy Policy link. */
    bodyPrefix: string;
    bodySuffix: string;
    /** Shown when Continue is pressed with the box unticked. */
    error: string;
    /**
     * Link text for the Privacy Policy anchor inside the sentence. Defaults to
     * "Privacy Policy"; AU sets "privacy policy" to read as one lower-case run
     * inside its sentence.
     */
    linkText?: string;
    /**
     * Whether the box starts ticked. The UK deliberately leaves it unticked —
     * a pre-ticked required box is a formality, not a gate. AU's client asked
     * for it pre-ticked but still blocking (item, September 2026), so this is a
     * per-market choice rather than a rule. Applied on SET_COUNTRY.
     */
    defaultChecked?: boolean;
  };
  /**
   * Whether the international dial code beside the phone field can be edited.
   *
   * Off everywhere by default: a fixed code is one fewer thing to get wrong,
   * and every market's customers overwhelmingly hold that market's numbers.
   * Where it is on, the number is validated against whatever code the customer
   * typed rather than the market's own region — otherwise a +353 number would
   * be checked against UK rules and pass or fail for the wrong reason.
   */
  dialCodeEditable?: boolean;
  /**
   * Short form of the postcode label, used in the studio search placeholder.
   *
   * Only the US differs: it labels the field "Zip Code" but has always asked
   * for a "Zip" in the search box, and that copy is live on converting
   * traffic. Everywhere else the label is already short enough and this is
   * absent, so the label itself is used.
   */
  postalCodeShortLabel?: string;
  /**
   * Heading over the contact block on the confirmation.
   *
   * Defaults to "Questions?". The UK asked for "Cancellations or Questions",
   * because in that market cancelling is the thing the block is actually for —
   * rescheduling is self-service through the email link, and the address below
   * exists to cancel.
   */
  questionsHeading?: string;
  /**
   * Centre the Step 1 consent block on the form instead of aligning it to the
   * Email field.
   *
   * Canada only. Its two consents are unlabelled sentences with nothing above
   * them, so left-aligning to Email just left the Name column conspicuously
   * empty. Australia's block has a heading and bold per-consent labels, which
   * give it an anchor — centring that put its left edge between two fields and
   * read as misaligned rather than centred, so this is a per-market choice
   * rather than a layout rule.
   */
  consentCentered?: boolean;
  /**
   * "What to Expect at Your Fitting" — each market's own page.
   *
   * Was hardcoded to clubchampion.com, so an international customer finishing
   * a booking was sent to the US site. All four markets publish the page at
   * the same path on their own domain (checked 2026-09-02).
   */
  fittingProcessUrl: string;
  /**
   * "How did you hear about us?" options, where the market's differ.
   *
   * Absent means the shared default. Canada's list is longer because CC's
   * Canadian picklist carries channels the others do not (PGA Partnership,
   * TXG/YouTube, A.I. Search). The strings are sent to Salesforce as-is, so
   * they have to match its picklist exactly — they are values, not labels.
   */
  hearAboutOptions?: readonly string[];
  /**
   * Whether Step 1 always collects the phone number — the four-field form.
   *
   * International markets launched with four fields, and moved off them on
   * 2026-10-10 (Dan Parks, for the international team and Joe Lee): the phone
   * now sits on the last step before Confirm Booking, see phoneOnConfirmStep.
   * The US stays on three and lets the phone-field-position GrowthBook
   * experiment decide, so the experiment keeps a clean single-market
   * population and international traffic never enters its buckets.
   */
  phoneOnFirstStep: boolean;
  /**
   * Whether the phone is collected, required, on the Confirm Appointment
   * step — the last screen before the booking is written.
   *
   * International only. Step 1 drops to Name / Email / Studio so the lead is
   * captured with less friction, and the number is asked for once the
   * customer has already chosen a time and is about to commit. It is still
   * required: this moves where the phone sits in the flow, not whether it is
   * collected. The US is untouched — it collects the phone optionally after
   * the booking, on the Get Set Up step, and that is a separate decision.
   *
   * A mount that pins `data-cc-phone="mandatory"` keeps the phone on Step 1
   * and is not asked again here; see collectsPhoneOnConfirm().
   */
  phoneOnConfirmStep: boolean;
  /**
   * Whether the post-booking ball-fitting upsell is offered. US only — the
   * $25 add-on is not sold in the international markets, so the section is
   * absent there rather than shown-and-disabled.
   */
  ballFittingOffered: boolean;
  /** How phone numbers are formatted and validated in this market. */
  phone: PhoneFormat;
  /**
   * Whether the SMS marketing opt-in is shown. UK is not sending SMS, and the
   * consent copy is US TCPA language (autodialer, "Msg & data rates apply")
   * that does not describe anything a UK customer is agreeing to.
   */
  smsOptInOffered: boolean;
  /** Who to contact from the "Questions?" block on the confirmation screen.
   *  Used only as a fallback — the booked store's own number wins when known. */
  supportContact: SupportContact;
  /** Privacy policy the consent copy links to. Each market publishes its own,
   *  and pointing an Australian customer at the US policy misstates what they
   *  are agreeing to. */
  privacyPolicyUrl: string;
  /**
   * How a customer changes or cancels, in that market's own words.
   *
   * Each market runs this differently — Canada takes cancellations by phone,
   * the UK by email — and the confirmation is the last place a generic line
   * is acceptable, because it is the only instruction the customer keeps.
   * `null` falls back to the studio's own email address.
   */
  changeBookingNote: string | null;
  /**
   * Pre-fit questionnaire, where the market sends customers to one instead of
   * the post-booking form.
   *
   * The form asks about a ball fitting, which is a US-only upsell. Outside the
   * US it collapses to a heading and a Submit button that submits nothing the
   * customer can see — CC's word was that it "is not clear what I am
   * submitting". These markets link to the questionnaire instead.
   */
  preFitQuestionnaireUrl: string | null;
  /**
   * Whether the confirm step asks "How did you hear about us?".
   *
   * International only. The US flow is live and converting, and an optional
   * marketing question does not go in front of that traffic without CC asking
   * for it there.
   */
  hearAboutOffered: boolean;
  /**
   * Whether the post-booking form asks for the customer's postcode.
   *
   * Separate from hearAboutOffered even though both are currently
   * international-only. They are two different asks — one is attribution, the
   * other is scheduling — and folding them into one flag would mean a market
   * could not take one without the other.
   */
  postalCodeOffered: boolean;
  /**
   * Whether the studio picker lists every studio in the market, with any
   * matches sorted to the top, instead of showing matches alone.
   *
   * Outside the US a handful of studios cover an entire country, so a search
   * that returns only nearby matches usually returns nothing — and "no results"
   * reads as "no studios here" rather than "none within reach". Showing the
   * full list keeps the real answer visible: there is one in Sydney, and it may
   * be worth the drive. The US has 139 studios, where a full list is noise.
   */
  showAllStudios: boolean;
  /**
   * Whether a promotional price is shown beside the fee charged without a
   * custom club purchase.
   *
   * Every market now shows both numbers. The US and Canada previously showed
   * the promotional price alone, on the reasoning that naming the
   * unconditional price beside it undercuts the promotion at the moment the
   * customer is choosing; the UK and Australian sites already presented both,
   * so a card showing one disagreed with the site around it. CC asked for the
   * two-line treatment everywhere on 2026-08-27, which also lands ahead of the
   * US promotion ending on 2026-09-01 — outside a promotion both numbers are
   * the offer anyway.
   *
   * Kept as a per-market flag rather than deleted: the markets diverged once
   * and can again, and it stays readable at runtime from
   * CC_WIDGET_CONFIG.promoPriceShowsFlatFee, which flips a single site without
   * a deploy. See promoPriceShowsFlatFee() below.
   */
  promoPriceShowsFlatFee: boolean;
  /**
   * Whether a customer who dead-ends on "No times available" is offered the
   * waitlist.
   *
   * Off in Australia (CC AU, 2026-10-22): the client asked for the waitlist
   * removed from that market. The no-availability message still shows; the
   * customer is left to pick another date or fitter.
   *
   * Everything downstream of the CTA — POST /waitlist, the WL- confirmation
   * screen, the analytics — is untouched, so switching a market back on is
   * this flag alone. See waitlistOffered() below.
   */
  waitlistOffered: boolean;
}

/**
 * Confirmation-screen contact. The US toll-free number is not reachable from
 * the UK and is not the number UK customers should be calling, so the market
 * decides both the channel and the value.
 */
export interface SupportContact {
  kind: 'tel' | 'email';
  /** Shown to the customer. */
  label: string;
  /** href target — digits for tel:, address for mailto:. */
  value: string;
}

/**
 * Per-market phone presentation.
 *
 * `nanp` keeps the North-American "(312) 555-0147". `international` renders
 * "+61 412 345 678" — the leading "+<dial code>" replacing the bracketed area
 * code, which is what UK and AU customers expect to see and type.
 *
 * `groups` are the digit-group sizes of the *national* number, after the
 * trunk prefix (the leading 0 that UK and AU numbers are written with
 * domestically) has been dropped.
 */
export interface PhoneFormat {
  style: 'nanp' | 'international';
  /** ISO 3166-1 alpha-2 region for libphonenumber. Note this is not always
   *  our own country code: the UK's ISO region is GB, not UK. */
  region: 'US' | 'GB' | 'CA' | 'AU';
  /** E.164 calling code, digits only, no '+'. */
  dialCode: string;
  /** Digit-group sizes, e.g. [3, 3, 3] → "412 345 678". */
  groups: number[];
  /** Total national digits expected — the sum of `groups`. */
  nationalDigits: number;
  /** Placeholder shown in the input. */
  placeholder: string;
}

export const COUNTRY_CONFIGS: Record<CountryCode, CountryConfig> = {
  us: {
    code: 'us',
    initialPromoCode: 'CUSTOMFIT',
    currency: 'USD',
    locale: 'en-US',
    postalCodeLabel: 'Zip Code',
    // "Enter Zip or City" — the live US placeholder, unchanged.
    postalCodeShortLabel: 'Zip',
    distanceUnit: 'mi',
    maxSearchRadius: 100,          // 139 US stores — 100mi covers metro areas
    emailOptInDefaultChecked: true,
    // Three fields by default; the phone-field-position experiment may add
    // the fourth. This is the only market the experiment applies to.
    phoneOnFirstStep: false,
    phoneOnConfirmStep: false,
    ballFittingOffered: true,
    phone: {
      style: 'nanp',
      region: 'US',
      dialCode: '1',
      groups: [3, 3, 4],
      nationalDigits: 10,
      placeholder: '(312) 555-0147',
    },
    smsOptInOffered: true,
    supportContact: { kind: 'tel', label: '(888) 340-7820', value: '8883407820' },
    changeBookingNote: null,
    preFitQuestionnaireUrl: null,
    hearAboutOffered: false,
    postalCodeOffered: false,
    privacyPolicyUrl: 'https://clubchampion.com/privacy-policy',
    fittingProcessUrl: 'https://clubchampion.com/golf-club-fitting-process',
    showAllStudios: false,
    promoPriceShowsFlatFee: true,  // Two lines in every market (CC, 2026-08-27).
    waitlistOffered: true,
  },
  uk: {
    code: 'uk',
    initialPromoCode: 'CUSTOMFITUK',
    currency: 'GBP',
    locale: 'en-GB',
    // Two words. CC marked this on both the studio field and the confirmation
    // (UK edits, September 2026). LocationField builds its placeholder from an
    // equality check against this string — change one without the other and
    // the UK renders "Enter Zip or City".
    postalCodeLabel: 'Post Code',
    distanceUnit: 'mi',            // The UK writes road distances in miles.
    maxSearchRadius: 500,          // ~800km, unchanged in reach: Glasgow→Basingstoke is ~370mi
    emailOptInDefaultChecked: true,
    // Phone moved from Step 1 to the Confirm step, 2026-10-10. Still required.
    phoneOnFirstStep: false,
    phoneOnConfirmStep: true,
    ballFittingOffered: false,
    // Marketing consent only — one plain sentence, no heading and no label,
    // because the privacy consent below it is a separate checkbox now and the
    // old "Please visit our Privacy Policy for more information" tail would
    // duplicate it. Copy as supplied.
    consent: {
      emailBody:
        'By checking the box you consent to receive marketing email messages from Club Champion',
    },
    // Blocks Continue until ticked, and is never sent anywhere.
    requiredConsent: {
      bodyPrefix: 'By checking the box you confirm that you have read and agree to Club Champion ',
      bodySuffix: '',
      error: 'Please confirm you have read and agree to the Privacy Policy.',
    },
    /** The dial code beside the phone field is editable in this market, so a
     *  customer with a non-UK mobile can enter it. Validation follows the code
     *  they type rather than the market's own region. */
    dialCodeEditable: true,
    // UK mobiles are 10 national digits written 07911 123456; internationally
    // that is +44 7911 123456 — a 4+6 split, unlike Australia's even 3s.
    phone: {
      style: 'international',
      region: 'GB',
      dialCode: '44',
      groups: [4, 6],
      nationalDigits: 10,
      // Instructional rather than a format sample, at CC's request. The dial
      // code sits in its own box beside the field, so a sample number here
      // read as a second, conflicting prefix.
      placeholder: 'Enter Phone Number',
    },
    smsOptInOffered: false,
    supportContact: {
      kind: 'email',
      label: 'info@clubchampiongolf.co.uk',
      value: 'info@clubchampiongolf.co.uk',
    },
    questionsHeading: 'Cancellations or Questions',
    changeBookingNote:
      'Modification can be done following the link on the email you have just received. '
      + 'For cancellations please email info@clubchampiongolf.co.uk.',
    preFitQuestionnaireUrl: 'https://clubchampiongolf.my.site.com/s/pre-fit-questionnaire',
    hearAboutOffered: true,
    postalCodeOffered: true,
    privacyPolicyUrl: 'https://clubchampiongolf.co.uk/privacy-policy',
    fittingProcessUrl: 'https://clubchampiongolf.co.uk/golf-club-fitting-process',
    showAllStudios: true,
    promoPriceShowsFlatFee: true,  // The UK site shows both numbers.
    waitlistOffered: true,
  },
  ca: {
    code: 'ca',
    // Region-tagged code; SF prices it per market — swap pricing there without
    // a widget deploy.
    initialPromoCode: 'CUSTOMFITCA',
    currency: 'USD',
    locale: 'en-CA',
    postalCodeLabel: 'Postcode',
    // CC asked for the Step 1 consent block to sit centred here.
    consentCentered: true,
    // CC's Canadian list, verbatim and in their order (Dan / Jonathon Snook,
    // 2026-09-02). Not merged with the shared five — Canada replaces them
    // outright, so TV, Word of Mouth and Print Ad are not offered there.
    //
    // These strings go to Salesforce as the answer, so they are values rather
    // than labels and are reproduced exactly. "Other" sits second-to-last in
    // the list as supplied; left where CC put it rather than moved to the end.
    hearAboutOptions: [
      'Industry Contact',
      'TXG/YouTube',
      'A.I. Search',
      'Repeat Client',
      'Previous Client Referral',
      'PGA Partnership',
      'Google/Bing Search',
      'Other',
      'Social Media Platforms',
    ],
    // Kilometres. CC said miles for both the UK and Canada on 2026-08-24 and
    // reversed the Canadian half in the September review — Canada is metric,
    // the UK is not, so they now differ.
    distanceUnit: 'km',
    // A round 600km, which is 373 miles against the 350 this was. Checked
    // before rounding: Canada has four studios and they are far apart, so the
    // extra 23 miles reaches nothing new — from Calgary the next nearest is
    // Vancouver at 681km, outside either figure. Relabelling it 350km instead
    // of converting would have been the real mistake: that is 217 miles, a
    // third off the search.
    maxSearchRadius: 600,
    emailOptInDefaultChecked: true,
    // Phone moved from Step 1 to the Confirm step, 2026-10-10. Still required.
    phoneOnFirstStep: false,
    phoneOnConfirmStep: true,
    ballFittingOffered: false,
    // Canada is in the North American Numbering Plan, so it keeps the
    // bracketed area code. Only UK and AU move to the +XX form.
    phone: {
      style: 'nanp',
      region: 'CA',
      dialCode: '1',
      groups: [3, 3, 4],
      nationalDigits: 10,
      // Instructional rather than a sample number, as for the UK. CC read the
      // example as a value already in the field.
      placeholder: 'Enter Phone Number',
    },
    // CA and AU inherit the US contact and SMS behaviour until their own
    // markets confirm otherwise — the UK feedback was UK-specific.
    smsOptInOffered: true,
    supportContact: { kind: 'tel', label: '(888) 340-7820', value: '8883407820' },
    // CC 2026-08-24: Canada links its own terms of use, not the US policy.
    changeBookingNote:
      'Cancellations or modifications can be made by calling (416) 331-9229.',
    preFitQuestionnaireUrl: 'https://clubchampiongolf.my.site.com/s/pre-fit-questionnaire',
    hearAboutOffered: true,
    postalCodeOffered: true,
    privacyPolicyUrl: 'https://clubchampion.ca/website-terms-of-use',
    fittingProcessUrl: 'https://clubchampion.ca/golf-club-fitting-process',
    showAllStudios: true,
    promoPriceShowsFlatFee: true,  // Two lines in every market (CC, 2026-08-27).
    waitlistOffered: true,
  },
  au: {
    code: 'au',
    initialPromoCode: 'CUSTOMFITAU',
    currency: 'AUD',
    locale: 'en-AU',
    postalCodeLabel: 'Postcode',
    distanceUnit: 'km',
    // 3000km, raised from 1000 on 2026-09-03.
    //
    // Steve reported city search returning nothing unless the address was
    // within 1000km of a studio, which was the radius doing exactly what it
    // said. With four studios on a continent 4000km wide that excluded most of
    // the map: Perth to Adelaide alone is ~2100km, so the interior, the north
    // and the whole west coast dead-ended on "no results" rather than being
    // offered the nearest studio and left to decide.
    //
    // 3000km reaches every populated part of the country from at least one
    // studio without spanning the widest pair (Perth–Sydney, ~3300km), so the
    // ordering still means something rather than always returning all four.
    maxSearchRadius: 3000,
    emailOptInDefaultChecked: true,
    // Phone moved from Step 1 to the Confirm step, 2026-10-10. Still required.
    phoneOnFirstStep: false,
    phoneOnConfirmStep: true,
    ballFittingOffered: false,
    // AU mobiles are 9 national digits written 0412 345 678; internationally
    // +61 412 345 678.
    phone: {
      style: 'international',
      region: 'AU',
      dialCode: '61',
      groups: [3, 3, 3],
      nationalDigits: 9,
      placeholder: 'Enter Phone Number',
    },
    smsOptInOffered: true,
    // Redesigned per the client meeting (September 2026): ONE combined
    // marketing box (email + SMS) plus a REQUIRED privacy box. Deliberately
    // verbatim — this is consent wording, reproduced rather than edited for
    // house style. `combined` folds email + SMS into the single box below,
    // which drives both opt-ins; the separate SMS box and the old footnote
    // are gone.
    consent: {
      combined: true,
      emailBody:
        'By checking this box you consent to receive marketing emails and sms from '
        + 'Club Champion Australia.',
    },
    // The privacy consent is required to proceed — Continue is blocked until it
    // is ticked — but, unlike the UK's, it starts ticked at the client's
    // request. Modelled on the UK required-consent mechanism (LeadCapture's
    // privacyOptIn + the privacyValid gate); AU differs only in defaultChecked
    // and its own copy.
    requiredConsent: {
      defaultChecked: true,
      bodyPrefix: 'By checking this box you consent to the Club Champion Australia ',
      bodySuffix: '.',
      linkText: 'privacy policy',
      error: 'Please confirm you consent to the Club Champion Australia privacy policy.',
    },
    // Club Champion Australia's own customer-service line (clubchampion.com.au),
    // not the US (888) number that stood in here at launch — a US toll-free
    // number on an Australian confirmation was the placeholder CC flagged.
    supportContact: { kind: 'tel', label: '1300 GOLF LAB', value: '13004653522' },
    changeBookingNote: null,
    preFitQuestionnaireUrl: 'https://clubchampiongolf.my.site.com/s/pre-fit-questionnaire',
    hearAboutOffered: true,
    postalCodeOffered: true,
    privacyPolicyUrl: 'https://clubchampion.com.au/privacy-policy',
    fittingProcessUrl: 'https://clubchampion.com.au/golf-club-fitting-process',
    showAllStudios: true,
    promoPriceShowsFlatFee: true,  // The AU site shows both numbers.
    waitlistOffered: false,        // Removed for AU at the client's request (2026-10-22).
  },
};

/**
 * Resolve a raw string (from a data attribute) to a CountryConfig.
 * Returns null on empty/unknown input — caller falls back to the page-wide
 * CC_WIDGET_CONFIG.initialPromoCode and default US behavior.
 */
export function resolveCountryConfig(raw: string | null | undefined): CountryConfig | null {
  if (!raw) return null;
  const normalized = raw.trim().toLowerCase();
  if (normalized in COUNTRY_CONFIGS) {
    return COUNTRY_CONFIGS[normalized as CountryCode];
  }
  // Silently ignore unknown codes rather than throw — widget stays functional
  // even if a host page mistypes the attribute.
  console.warn(`[CC widget] Unknown countrycode "${raw}", falling back to page-wide config.`);
  return null;
}

/** Default US config — returned by countryConfigOrDefault when state.country
 * is null/unset. Keeps callers concise: `const c = countryConfigOrDefault(state.country)`
 * instead of ternaries against COUNTRY_CONFIGS.us. */
export function countryConfigOrDefault(raw: string | null | undefined): CountryConfig {
  return resolveCountryConfig(raw) ?? COUNTRY_CONFIGS.us;
}

/**
 * Whether this market collects email + SMS marketing consent as ONE checkbox.
 *
 * Australia only. When true the single marketing box on Step 1 drives BOTH the
 * email opt-in and the SMS opt-in, so the booking payload sources both from
 * `promoOptIn` (the box's state) rather than from a separate SMS control that
 * the AU form no longer renders. Keeping this a config-derived predicate means
 * the payload sites stay market-agnostic.
 */
export function marketingConsentCombined(country: string | null | undefined): boolean {
  return countryConfigOrDefault(country).consent?.combined === true;
}

/**
 * Whether the Confirm Appointment step collects the phone number.
 *
 * True only where the market asks for it there (UK, CA, AU) AND the phone was
 * not already taken on Step 1 — a mount pinned to `data-cc-phone="mandatory"`
 * keeps the four-field form, and asking twice would be worse than either
 * placement. Never in reschedule mode: the appointment on file already has a
 * number, and Step 1 of a reschedule is an identity check, not a lead form.
 */
export function collectsPhoneOnConfirm(
  country: string | null | undefined,
  phoneOnFirstStep: boolean,
  rescheduleMode: boolean,
): boolean {
  if (phoneOnFirstStep || rescheduleMode) return false;
  return countryConfigOrDefault(country).phoneOnConfirmStep;
}

/**
 * Whether to show the flat fitting fee beside a promotional price.
 *
 * Every market defaults to true as of 2026-08-27. Still overridable at runtime:
 * CC_WIDGET_CONFIG.promoPriceShowsFlatFee flips a single host page without a
 * deploy, and reverts by deleting the key. Anything other than a boolean is
 * ignored, so a typo in the host page falls through to the market default
 * rather than silently changing prices.
 */
export function promoPriceShowsFlatFee(country: string | null | undefined): boolean {
  if (typeof window !== 'undefined') {
    const override = (window as { CC_WIDGET_CONFIG?: { promoPriceShowsFlatFee?: unknown } })
      .CC_WIDGET_CONFIG?.promoPriceShowsFlatFee;
    if (typeof override === 'boolean') return override;
  }
  return countryConfigOrDefault(country).promoPriceShowsFlatFee;
}

/**
 * Whether the no-availability state offers the waitlist in this market.
 *
 * Australia is the only market without one. An unknown or missing country is
 * the US default, which offers it — the same fallback every other predicate
 * here uses.
 */
export function waitlistOffered(country: string | null | undefined): boolean {
  return countryConfigOrDefault(country).waitlistOffered;
}

/**
 * Pre-fit questionnaire link for a booking, or null where the market does not
 * use one.
 *
 * The questionnaire is a Salesforce Experience site and it looks the customer
 * up by the ids on their booking — so the link has to point at the SAME
 * Salesforce org the booking was written to. Staging writes to the QA sandbox,
 * whose records do not exist in production, so a production link built from a
 * staging booking errors out. That is not a broken link; it is the wrong org.
 *
 * The environment is inferred from the API the widget is talking to rather
 * than from a separate flag, because that is the thing that actually decides
 * which org the booking landed in.
 */
export function preFitQuestionnaireLink(
  country: string | null | undefined,
  accountId: string | undefined,
  appointmentNumber: string | undefined,
  apiBaseUrl?: string,
): string | null {
  const base = countryConfigOrDefault(country).preFitQuestionnaireUrl;
  if (!base) return null;

  const isSandbox = (apiBaseUrl ?? '').includes('-stg.');
  const host = isSandbox
    ? base.replace('clubchampiongolf.my.site.com', 'clubchampiongolf--qa.sandbox.my.site.com')
    : base;

  const params = new URLSearchParams();
  if (accountId) params.set('accId', accountId);
  if (appointmentNumber) params.set('accUniqueNumber', appointmentNumber);
  const query = params.toString();

  return query ? `${host}?${query}` : host;
}
